import { createSignal, onCleanup, onMount, For, Show } from 'solid-js';
import { createStore, reconcile } from 'solid-js/store';
import { useSFetch } from '../hooks/sfetch';
import { useToast } from '../components/MessageBox';
import { showConfirm } from '../components/Dialog';
import Text from '../components/Text';
import { EmptyState } from '../components/EmptyState';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import { downloadFromApi } from '../services/downloadFile';
import { firstError } from '../api/envelope';

interface MonitorRow {
    botId: string;
    botUsername: string;
    startedByUserId: string;
    startedAtUtc: string;
    expiresAtUtc: string;
    serverSamples: number;
    clientSamples: number;
}

interface MonitorableBot {
    botId: string;
    username: string;
}

function remaining(expiresAtUtc: string, now: number): string {
    let s = Math.max(0, Math.floor((new Date(expiresAtUtc).getTime() - now) / 1000));
    const h = Math.floor(s / 3600); s -= h * 3600;
    const m = Math.floor(s / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function AdminDiagnosticMonitors() {
    const sfetch = useSFetch();
    const toast = useToast();
    const { t } = useTranslation();

    const [monitors, setMonitors] = createStore<MonitorRow[]>([]);
    const [bots, setBots] = createSignal<MonitorableBot[]>([]);
    const [selectedBot, setSelectedBot] = createSignal('');
    const [hours, setHours] = createSignal(24);
    const [loading, setLoading] = createSignal(true);
    const [busy, setBusy] = createSignal<string | null>(null);
    const [now, setNow] = createSignal(Date.now());

    const base = '/api/vektor/admin/monitor';

    async function fetchMonitors() {
        const res = await sfetch(base);
        if (res.ok && res.value) {
            setMonitors(reconcile((res.value.monitors ?? []) as MonitorRow[], { key: 'botId' }));
        }
        setLoading(false);
    }

    async function fetchBots() {
        const res = await sfetch(`${base}/bots`);
        if (res.ok && res.value) setBots((res.value.bots ?? []) as MonitorableBot[]);
    }

    async function startMonitor() {
        const botId = selectedBot();
        if (!botId) return;
        setBusy(botId);
        const res = await sfetch(`${base}/${botId}/start?hours=${hours()}`, { method: 'POST' });
        setBusy(null);
        if (!res.ok) {
            toast?.error(firstError(res) ?? t('VEKTOR_MONITOR_START_FAILED'));
            return;
        }
        const name = bots().find(b => b.botId === botId)?.username ?? botId;
        toast?.success(t('VEKTOR_MONITOR_STARTED_TOAST', name));
        await fetchMonitors();
    }

    async function stopMonitor(m: MonitorRow) {
        const confirmed = await showConfirm(t('VEKTOR_MONITOR_STOP_CONFIRM', m.botUsername), true);
        if (!confirmed) return;
        setBusy(m.botId);
        const res = await sfetch(`${base}/${m.botId}/stop`, { method: 'POST' });
        setBusy(null);
        if (!res.ok) {
            toast?.error(firstError(res) ?? t('VEKTOR_MONITOR_STOP_FAILED'));
            return;
        }
        toast?.info(t('VEKTOR_MONITOR_STOPPED_TOAST', m.botUsername));
        await fetchMonitors();
    }

    async function exportMonitor(m: MonitorRow) {
        const failed = await downloadFromApi(`${base}/${m.botId}/export`, `goggles-monitor-${m.botUsername}.json`);
        if (failed) toast?.error(failed === 404 ? t('VEKTOR_MONITOR_NO_DATA') : t('STATE_ERROR_GENERIC'));
    }

    onMount(() => {
        void fetchBots();
        void fetchMonitors();
        const refresh = window.setInterval(() => {
            if (document.visibilityState === 'visible') void fetchMonitors();
        }, 3000);
        const ticker = window.setInterval(() => {
            if (document.visibilityState === 'visible') setNow(Date.now());
        }, 1000);
        onCleanup(() => { clearInterval(refresh); clearInterval(ticker); });
    });

    const monitoredIds = () => new Set(monitors.map(m => m.botId));

    return (
        <div class="v" style={{ 'max-width': '1100px', margin: '0 auto', padding: 'var(--spacing-xl)', gap: 'var(--spacing-lg)' }}>
            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                <h1 style={{ margin: 0 }}><Text val="VEKTOR_MONITOR_TITLE" /></h1>
                <span style={{ color: 'var(--dark-text-muted)', 'font-size': 'var(--font-size-sm)' }}>
                    <Text val="VEKTOR_MONITOR_SUBTITLE" />
                </span>
            </div>

            <div class="fcard h" style={{ 'align-items': 'flex-end', gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                <label class="v" style={{ gap: 'var(--spacing-xs)', flex: '1', 'min-width': '14rem' }}>
                    <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="VEKTOR_MONITOR_PICK_BOT" /></span>
                    <select value={selectedBot()} onInput={(e) => setSelectedBot(e.currentTarget.value)}>
                        <option value="">--</option>
                        <For each={bots().filter(b => !monitoredIds().has(b.botId))}>{(b) =>
                            <option value={b.botId}>{b.username}</option>
                        }</For>
                    </select>
                </label>
                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span style={{ 'font-size': 'var(--font-size-sm)', color: 'var(--dark-text-muted)' }}><Text val="VEKTOR_MONITOR_DURATION" /></span>
                    <select value={hours()} onInput={(e) => setHours(parseInt(e.currentTarget.value, 10))}>
                        <option value="24">24h</option>
                        <option value="48">48h</option>
                        <option value="72">72h</option>
                    </select>
                </label>
                <button class="btn btn-primary" disabled={!selectedBot() || busy() === selectedBot()} onClick={() => void startMonitor()}>
                    <i class="fa-solid fa-circle-dot" /> <Text val="VEKTOR_MONITOR_START" />
                </button>
            </div>

            <Show when={!loading()} fallback={
                <div class="fcard" style={{ 'text-align': 'center', color: 'var(--dark-text-muted)' }}><Text val="LOADING" /></div>
            }>
                <Show when={monitors.length > 0} fallback={
                    <EmptyState icon="fa-wave-square" title={t('VEKTOR_MONITOR_EMPTY')} />
                }>
                    <div class="v" style={{ gap: 'var(--spacing-md)' }}>
                        <For each={monitors}>{(m) => {
                            return (
                                <div class="fcard h" style={{ 'align-items': 'center', gap: 'var(--spacing-lg)' }}>
                                    <i class="fa-solid fa-wave-square" style={{ color: 'var(--success-500)', 'font-size': 'var(--font-size-lg)' }} />
                                    <div class="v" style={{ flex: '1', gap: 'var(--spacing-xs)', 'min-width': 0 }}>
                                        <div style={{ 'font-weight': 'var(--font-weight-semibold)' }}>{m.botUsername}</div>
                                        <div style={{ 'font-size': 'var(--font-size-xs)', color: 'var(--dark-text-muted)', 'font-family': 'var(--font-family-mono)' }}>
                                            <Text val="VEKTOR_MONITOR_STARTED" />: {formatDateTime(m.startedAtUtc)}
                                            {' · '}<Text val="VEKTOR_MONITOR_EXPIRES_IN" />: {remaining(m.expiresAtUtc, now())}
                                            {' · '}{m.clientSamples}/{m.serverSamples} <Text val="VEKTOR_MONITOR_SAMPLES" />
                                        </div>
                                    </div>
                                    <button class="btn btn-secondary btn-sm" onClick={() => void exportMonitor(m)}>
                                        <i class="fa-solid fa-download" /> <Text val="VEKTOR_MONITOR_EXPORT" />
                                    </button>
                                    <button class="btn btn-danger btn-sm" disabled={busy() === m.botId} onClick={() => void stopMonitor(m)}>
                                        <i class="fa-solid fa-stop" /> <Text val="VEKTOR_MONITOR_STOP" />
                                    </button>
                                </div>
                            );
                        }}</For>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
